import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, TrendingUp, Users, Zap, Building2, ShoppingCart, BarChart3, Smartphone, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Projects = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const stagger = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const projects = [
    {
      title: 'Plateforme E-commerce Omnicanal',
      sector: 'Retail & Commerce',
      icon: <ShoppingCart />,
      description: 'Refonte complète d\'une boutique en ligne avec optimisation des parcours utilisateurs, intégration de paiement sécurisé et système de gestion des stocks en temps réel.',
      challenges: [
        'Intégration de multiples passerelles de paiement',
        'Synchronisation temps réel avec le stock physique',
        'Optimisation des performances pour 10K+ produits',
      ],
      technologies: ['React', 'Node.js', 'MongoDB', 'Stripe', 'AWS'],
      impact: '+32% de conversion',
      secondaryImpact: '+45% de panier moyen',
      duration: '4 mois',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop',
    },
    {
      title: 'Dashboard Analytics Financier',
      sector: 'Finance & Banque',
      icon: <BarChart3 />,
      description: 'Création d\'une plateforme de visualisation de données financières en temps réel avec tableaux de bord interactifs et rapports automatisés pour la prise de décision.',
      challenges: [
        'Traitement de millions de transactions quotidiennes',
        'Visualisations complexes et interactives',
        'Sécurité des données sensibles',
      ],
      technologies: ['Vue.js', 'Python', 'PostgreSQL', 'D3.js', 'Docker'],
      impact: '–40% de temps d\'analyse',
      secondaryImpact: '+25% de précision des prévisions',
      duration: '5 mois',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop',
    },
    {
      title: 'Application Mobile RH',
      sector: 'Ressources Humaines',
      icon: <Users />,
      description: 'Application mobile de gestion des congés, évaluations de performance et communication interne pour 500+ employés avec synchronisation cloud.',
      challenges: [
        'Interface intuitive pour tous les profils utilisateurs',
        'Système de notifications intelligent',
        'Conformité RGPD et sécurité',
      ],
      technologies: ['React Native', 'FastAPI', 'Firebase', 'Redux'],
      impact: '–60% de tâches manuelles',
      secondaryImpact: '+80% de satisfaction employés',
      duration: '3 mois',
      image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600&h=400&fit=crop',
    },
    {
      title: 'Plateforme de Réservation SaaS',
      sector: 'Hospitality & Tourisme',
      icon: <Building2 />,
      description: 'Système de réservation en ligne pour chaîne d\'hôtels avec gestion des disponibilités, tarification dynamique et intégration CRM.',
      challenges: [
        'Gestion des disponibilités multi-établissements',
        'Tarification dynamique en fonction de la demande',
        'Intégration avec systèmes existants',
      ],
      technologies: ['Angular', 'Django', 'MySQL', 'Redis', 'Kubernetes'],
      impact: '+28% de réservations directes',
      secondaryImpact: '–22% de coûts OTA',
      duration: '6 mois',
      image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&h=400&fit=crop',
    },
    {
      title: 'Système IoT de Gestion Énergétique',
      sector: 'Industrie & Énergie',
      icon: <Zap />,
      description: 'Plateforme de monitoring et optimisation de la consommation énergétique avec capteurs IoT et machine learning pour prédictions.',
      challenges: [
        'Intégration de milliers de capteurs IoT',
        'Traitement de données en temps réel',
        'Modèles prédictifs ML',
      ],
      technologies: ['React', 'Python', 'TensorFlow', 'InfluxDB', 'MQTT'],
      impact: '–35% de consommation énergétique',
      secondaryImpact: '+50% de prédiction des pannes',
      duration: '8 mois',
      image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=600&h=400&fit=crop',
    },
    {
      title: 'Marketplace B2B',
      sector: 'Commerce B2B',
      icon: <Smartphone />,
      description: 'Marketplace connect ant fournisseurs et acheteurs professionnels avec système de devis, négociation et gestion des contrats.',
      challenges: [
        'Matching intelligent fournisseurs-acheteurs',
        'Workflow de négociation complexe',
        'Gestion multi-devises et multi-langues',
      ],
      technologies: ['Next.js', 'Express', 'PostgreSQL', 'Elasticsearch', 'AWS'],
      impact: '+18% de transactions',
      secondaryImpact: '–42% de délai de négociation',
      duration: '7 mois',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop',
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-white via-cyan-50 to-blue-50 py-24 sm:py-32">
        <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            animate="animate"
            variants={stagger}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div variants={fadeInUp} className="inline-flex items-center space-x-2 bg-cyan-100 text-cyan-700 px-4 py-2 rounded-full text-sm font-medium mb-8">
              <Briefcase size={16} />
              <span>Nos réalisations</span>
            </motion.div>
            <motion.h1
              variants={fadeInUp}
              className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6 leading-tight"
            >
              Projets qui transforment
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-600 leading-relaxed"
            >
              Découvrez comment nous avons aidé nos clients à atteindre leurs objectifs grâce à des solutions technologiques sur mesure et performantes.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 gap-8"
          >
            {projects.map((project, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -4, boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }}
                className="bg-white rounded-2xl border border-gray-200 overflow-hidden transition-all duration-300 hover:border-cyan-300 flex flex-col h-full"
              >
                {/* Image Container */}
                <div className="relative overflow-hidden aspect-video">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                  <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-lg flex items-center justify-center text-white">
                        {project.icon}
                      </div>
                    </div>
                    <div className="text-xs font-medium bg-white/90 backdrop-blur-sm text-gray-900 px-3 py-1.5 rounded-full">
                      {project.duration}
                    </div>
                  </div>
                  <div className="absolute bottom-4 left-4">
                    <div className="inline-block text-xs font-medium bg-cyan-500/90 backdrop-blur-sm text-white px-3 py-1.5 rounded-full">
                      {project.sector}
                    </div>
                  </div>
                </div>

                {/* Content Container */}
                <div className="p-6 flex flex-col flex-grow">
                  {/* Title & Description */}
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3 leading-tight">
                      {project.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {project.description}
                    </p>
                  </div>

                  {/* Challenges */}
                  <div className="mb-5">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                      Défis relevés
                    </h4>
                    <ul className="space-y-2">
                      {project.challenges.map((challenge, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-sm text-gray-600">
                          <TrendingUp size={14} className="text-cyan-600 mt-0.5 flex-shrink-0" />
                          <span>{challenge}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Technologies */}
                  <div className="mb-6">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                      Technologies
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Impact - Pushed to bottom */}
                  <div className="grid grid-cols-2 gap-3 mt-auto">
                    <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-4 rounded-xl border border-cyan-200 text-center">
                      <div className="text-xl font-bold text-cyan-600 mb-1">{project.impact}</div>
                      <div className="text-xs text-gray-600">Impact principal</div>
                    </div>
                    <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-4 rounded-xl border border-cyan-200 text-center">
                      <div className="text-xl font-bold text-cyan-600 mb-1">{project.secondaryImpact}</div>
                      <div className="text-xs text-gray-600">Impact secondaire</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-cyan-500 via-blue-600 to-blue-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Votre projet, notre expertise
            </h2>
            <p className="text-xl mb-10 text-cyan-100 max-w-3xl mx-auto">
              Chaque projet est unique. Discutons de vos besoins et créons ensemble une solution parfaitement adaptée.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-white text-cyan-600 rounded-xl font-semibold hover:shadow-2xl transform hover:scale-105 transition-all"
            >
              <span>Démarrer votre projet</span>
              <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Projects;